import { Alert, Button, Spin } from 'antd';
import { StyledActivityTryFeedback } from './ActivityTryFeedback.styled';
import typeLabel from '@/constants/ActivityTypeLabelsStudents';
import ProgressFeedback from '../../ProgressFeedback/ProgressFeedback';
import { calculateAveragesWithSlots } from '@/modules/progress/calculations/studentTaskProgress';

export default function ActivityTryFeedback({
  taskActivity,
  visible,
  actions,
  dueDate,
  savingProgress,
  syncError,
  taskProgress,
}) {
  if (savingProgress) {
    return (
      <StyledActivityTryFeedback visible={visible}>
        <div className="saving">
          <h3>Your progress is being saved</h3>
          <Spin />
        </div>
      </StyledActivityTryFeedback>
    );
  }

  const completedTries = getCompletedTries(taskActivity?.progresses);
  const repetitions = taskActivity?.activityConf?.options?.repetitions;
  const currentProgress = calculateAveragesWithSlots(
    taskActivity?.activityProgress ? [taskActivity.activityProgress] : []
  );

  const repetitionsCompleted = completedTries >= repetitions;
  const currentAllCorrect =
    !currentProgress.correctness || currentProgress.totalCorrect == 100;

  const taskAllDone = taskProgress.totalProgress == 100;
  const taskAllCorrect =
    !taskProgress.correctness || taskProgress.totalCorrect == 100;

  const activityLabel = typeLabel(
    taskActivity?.activity.type,
    taskActivity?.activity.data.subType,
    taskActivity?.activityConf.miniactivityType
  );

  return (
    <StyledActivityTryFeedback visible={visible}>
      <h3>
        {taskActivity?.activity?.name} {activityLabel}
      </h3>
      <ProgressFeedback
        repetitions={taskActivity?.activityConf?.options?.repetitions}
        activityProgress={taskActivity?.activityProgress}
        showCorrect={taskActivity?.activityConf?.correctness}
        dueDate={dueDate}
        isStudent
        onlyGraph
      />
      <div className="feedback-actions">
        {getButtonTypes({
          repetitionsCompleted,
          currentAllCorrect,
          taskAllDone,
          taskAllCorrect,
        }).map((buttonType) => (
          <div className="action" key={buttonType}>
            {
              {
                doNextRepetition: (
                  <>
                    <p>
                      Great start! That's {completedTries}{' '}
                      {completedTries > 1 ? 'tries' : 'try'} done ✅
                    </p>
                    <p>Give it another go&mdash;you've got this!</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.repeat}
                    >
                      Do the next try
                    </Button>
                  </>
                ),
                doItAgain: (
                  <>
                    {taskAllDone}
                    <p>Well done! You completed the {activityLabel} 🙌</p>
                    <p>Want to improve your score? Try it again!</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.repeat}
                    >
                      Do it again
                    </Button>
                  </>
                ),
                doNext: (
                  <>
                    <p>Nice work! You finished the {activityLabel} 🎉</p>
                    <p>You're learning so much—keep going!</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.goToNextActivity}
                    >
                      Do the next activity
                    </Button>
                  </>
                ),
                orDoNext: (
                  <>
                    <p>Or</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.goToNextActivity}
                    >
                      Do the next activity
                    </Button>
                  </>
                ),
                doNextUncorrect: (
                  <>
                    <p>Well done! Let's make the next one even better!</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.goToNextUncorrectActivity}
                    >
                      Do the next activity
                    </Button>
                  </>
                ),
                back: (
                  <>
                    <p>Or</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.goBack}
                    >
                      Go to the next task
                    </Button>
                  </>
                ),
                finish: (
                  <>
                    <p>Fantastic job! You totally crushed it! 🎉 </p>
                    <p>Keep up the awesome work!</p>
                    <Button
                      type="primary"
                      size="large"
                      onClick={actions.goBack}
                    >
                      Go to the next task
                    </Button>
                  </>
                ),
              }[buttonType]
            }
          </div>
        ))}

        {syncError ? (
          <Alert
            type="warning"
            message="The progress was saved in Rockalingua but it wasn't correctly saved in Google Classroom"
            description="You can continue but please warn your teacher about this issue."
          />
        ) : null}
      </div>
    </StyledActivityTryFeedback>
  );
}

function getCompletedTries(progresses) {
  return progresses?.reduce(
    (acc, progress) => acc + (progress.data.progress == 100 ? 1 : 0),
    0
  );
}

function getButtonTypes({
  repetitionsCompleted,
  currentAllCorrect,
  taskAllDone,
  taskAllCorrect,
}) {
  if (!repetitionsCompleted) {
    return ['doNextRepetition'];
  }

  if (taskAllDone && taskAllCorrect) {
    return ['finish'];
  }

  if (!currentAllCorrect) {
    if (taskAllDone) {
      return ['doItAgain', 'back'];
    } else {
      return ['doItAgain', 'orDoNext'];
    }
  }

  if (currentAllCorrect && !taskAllDone) {
    return ['doNext'];
  }

  if (currentAllCorrect && taskAllDone && !taskAllCorrect) {
    return ['doNextUncorrect', 'back'];
  }

  return [];
}
